import React, { useState, useEffect } from "react";
import { apiService } from "../../services/api";
import { ProhibitedSite } from "../../types";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  NoSymbolIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";

export const ProhibitedSitesTab: React.FC = () => {
  const [sites, setSites] = useState<ProhibitedSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<ProhibitedSite | null>(null);
  const [formData, setFormData] = useState({
    url_pattern: "",
    description: "",
    is_active: true,
  });
  const [blockAll, setBlockAll] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    try {
      setLoading(true);
      const [sitesResponse, settingsResponse] = await Promise.all([
        apiService.getProhibitedSites(),
        apiService.getCompanySettings(),
      ]);
      setSites(sitesResponse.data);
      setBlockAll(settingsResponse.data.block_prohibited_sites);
    } catch (err) {
      console.error("Error loading prohibited sites:", err);
      setError("Failed to load prohibited sites");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (site?: ProhibitedSite) => {
    if (site) {
      setEditingSite(site);
      setFormData({
        url_pattern: site.url_pattern,
        description: site.description || "",
        is_active: site.is_active,
      });
    } else {
      setEditingSite(null);
      setFormData({
        url_pattern: "",
        description: "",
        is_active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSite(null);
    setFormData({
      url_pattern: "",
      description: "",
      is_active: true,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingSite) {
        await apiService.updateProhibitedSite(editingSite.id, formData);
      } else {
        await apiService.createProhibitedSite(formData);
      }
      loadSites();
      handleCloseModal();
    } catch (err) {
      console.error("Error saving prohibited site:", err);
      alert("Failed to save prohibited site");
    }
  };

  const handleDelete = async (id: string) => {
    if (
      !window.confirm("Are you sure you want to delete this prohibited site?")
    )
      return;
    try {
      await apiService.deleteProhibitedSite(id);
      loadSites();
    } catch (err) {
      console.error("Error deleting prohibited site:", err);
      alert("Failed to delete prohibited site");
    }
  };

  const handleToggleStatus = async (site: ProhibitedSite) => {
    try {
      await apiService.updateProhibitedSite(site.id, {
        is_active: !site.is_active,
      });
      loadSites();
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status");
    }
  };

  const handleToggleBlock = async () => {
    try {
      const newValue = !blockAll;
      await apiService.updateProhibitedSitesSettings({
        block_prohibited_sites: newValue,
      });
      setBlockAll(newValue);
    } catch (err) {
      console.error("Error updating block settings:", err);
      alert("Failed to update block settings");
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      await apiService.importProhibitedSites(file);
      alert("Sites importados com sucesso!");
      loadSites();
    } catch (err) {
      console.error("Error importing sites:", err);
      alert("Falha ao importar sites");
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  if (loading)
    return (
      <div className="p-4 text-center text-gray-500">
        Loading prohibited sites...
      </div>
    );
  if (error) return <div className="p-4 text-center text-red-500">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Sites Proibidos
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Gerencie os sites que serão bloqueados nos computadores monitorados.
          </p>
        </div>
        <div className="flex space-x-3">
          <div className="flex items-center mr-4">
            <button
              type="button"
              onClick={handleToggleBlock}
              className={`${
                blockAll ? "bg-indigo-600" : "bg-gray-200"
              } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              role="switch"
              aria-checked={blockAll}
            >
              <span
                aria-hidden="true"
                className={`${
                  blockAll ? "translate-x-5" : "translate-x-0"
                } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
              />
            </button>
            <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
              Bloquear Sites Proibidos
            </span>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept=".txt,.csv,.json"
          />
          <button
            onClick={handleImportClick}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
            Importar
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Adicionar Site
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {sites.length === 0 ? (
            <li className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
              Nenhum site proibido cadastrado.
            </li>
          ) : (
            sites.map((site) => (
              <li
                key={site.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <div className="flex items-center">
                  <div
                    className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                      site.is_active
                        ? "bg-red-100 text-red-600"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <NoSymbolIcon className="h-6 w-6" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {site.url_pattern}
                    </p>
                    {site.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {site.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => handleToggleStatus(site)}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      site.is_active
                        ? "bg-green-100 text-green-800 hover:bg-green-200"
                        : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                    }`}
                  >
                    {site.is_active ? "Ativo" : "Inativo"}
                  </button>
                  <button
                    onClick={() => handleOpenModal(site)}
                    className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(site.id)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {isModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                    {editingSite
                      ? "Editar Site Proibido"
                      : "Adicionar Site Proibido"}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label
                        htmlFor="url_pattern"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        URL / Padrão
                      </label>
                      <input
                        type="text"
                        name="url_pattern"
                        id="url_pattern"
                        required
                        className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        placeholder="ex: facebook.com"
                        value={formData.url_pattern}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            url_pattern: e.target.value,
                          })
                        }
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Use * como curinga (ex: *.facebook.com)
                      </p>
                    </div>
                    <div>
                      <label
                        htmlFor="description"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Descrição (Opcional)
                      </label>
                      <input
                        type="text"
                        name="description"
                        id="description"
                        className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        id="is_active"
                        name="is_active"
                        type="checkbox"
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_active: e.target.checked,
                          })
                        }
                      />
                      <label
                        htmlFor="is_active"
                        className="ml-2 block text-sm text-gray-900 dark:text-gray-300"
                      >
                        Ativo
                      </label>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Salvar
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
